import * as _ from 'lodash'
import q from 'q'
import React from 'react'
import PropTypes from 'prop-types'
import { fabric } from 'fabric'
import { FineUploaderBasic as Uploader } from 'fine-uploader'
import orgamax from 'ampersand-app'
import request from '@/modules/base/util/request'
import lang from 'lang'
import letterConfig from '@/modules/letter/config'
import LetterElementModel from '@/modules/letter/model/letter_element_model'
import FabricText from '@/modules/letter/model/letter_fabric_text'
import fabricObjectToImageLetterElement from '@/modules/letter/util/fabric_object_to_image_letter_element'
import fabricObjectToTextLetterElement from '@/modules/letter/util/fabric_object_to_text_letter_element'
import fabricObjectToShapeLetterElement from '@/modules/letter/util/fabric_object_to_shape_letter_element'
import Loader from '@/modules/base/components/loader/loader'
import letterElementsToFabricObjects from '@/modules/letter/util/letter_elements_to_fabric_objects'
import HeaderFontTools from '@/modules/letter/components/header/header_font_tools'
import { letterHeaderConstants } from '@/modules/base/util/constants'

import { toast } from '@/modules/base/components/toast/toastConfig'

const { DEFAULT_FONT_SIZE } = letterHeaderConstants

const KEY_CODES = {
  ENTER: 13,
  DELETE: 46,
  TOP: 38,
  DOWN: 40,
  LEFT: 37,
  RIGHT: 39,
  ESCAPE: 27,
}

const SHIFT_STEP = 10
const CANVAS_HEIGHT = 198
const CANVAS_WIDTH = 750
const OFFSET = 0
const CANVAS_ZINDEX_OFFSET = 3
const BOUNDING_BOX = {
  left: OFFSET,
  top: OFFSET,
  width: CANVAS_WIDTH - OFFSET * 2,
  height: CANVAS_HEIGHT - OFFSET * 2,
}
const CENTER = {
  x: BOUNDING_BOX.left + BOUNDING_BOX.width / 2,
  y: BOUNDING_BOX.top + BOUNDING_BOX.height / 2,
}
const H_SNAP_POINTS = [OFFSET, CENTER.y, CANVAS_WIDTH - OFFSET, CENTER.y]
const V_SNAP_POINTS = [CENTER.x, OFFSET, CENTER.x, CANVAS_HEIGHT - OFFSET]

class HeaderEdit extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      loading: false,
      deleteButtonActive: false,
      textSelected: false,
      canHaveColor: false,
      hSnapVisible: false,
      vSnapVisible: false,
      wrapperVisible: false,
      textIsBold: false,
      textIsItalic: false,
      textIsUnderlined: false,
      textSupportsBold: false,
      textSupportsItalic: false,
      selectedFontSize: undefined,
      selectedFont: undefined,
      selectedFontColor: undefined,
      previousEl: undefined,
      fabricObjects: [],
    }

    this.uploader = undefined
    this.vSnap = undefined
    this.hSnap = undefined
    this.fonts = letterConfig.fonts
    this.whenImageUploaded = q.defer()
    this.isSaving = false
    this.hasAddedText = false
    this.addedTextAdditionalTop = 0
    this.addedTextAdditionalLeft = 0

    this.onDocumentClick = this.onDocumentClick.bind(this)
    this.deactivateAll = this.deactivateAll.bind(this)
    this.handleKeydown = this.handleKeydown.bind(this)
    this.saveLetterElements = this.saveLetterElements.bind(this)
    this.onCanvasMouseUp = this.onCanvasMouseUp.bind(this)
    this.onCanvasObjectMoving = this.onCanvasObjectMoving.bind(this)
    this.onCanvasObjectScaling = this.onCanvasObjectScaling.bind(this)
    this.onCanvasObjectSelected = this.onCanvasObjectSelected.bind(this)
    this.onCanvasSelectionCleared = this.onCanvasSelectionCleared.bind(this)
    this.onCanvasObjectModified = this.onCanvasObjectModified.bind(this)
    this.onButtonSave = this.onButtonSave.bind(this)
  }

  onDocumentClick(event) {
    const overlayEl = $('.documentOverlay')
    // only save the letter elements if the documentOverlay was clicked
    if (event.target === overlayEl[0] && !this.isSaving) {
      this.isSaving = true
      this.saveLetterElements()
    }
  }

  componentDidMount() {
    const { items } = this.props

    $(document).on('mousedown', this.onDocumentClick)
    $(document).on('keydown', this.handleKeydown)

    this.uploader = this.initUploader()
    this.initCanvas()

    letterElementsToFabricObjects(items).then(fabricObjects => {
      this.setState({ fabricObjects }, () => {
        this.addFabricObjectsToCanvas()
      })
    })
  }

  componentWillUnmount() {
    orgamax.off('documentClicked', this.saveLetterElements)
    $(document).off('mousedown', this.onDocumentClick)
    $(document).off('keydown', this.handleKeydown)
    this.removeDeselectHandlers()
      .canvas.clear()
      .dispose()
    this.uploader.reset()
    this.whenImageUploaded = q.defer()
    this.uploader = undefined
    this.vSnap = undefined
    this.hSnap = undefined
    this.isSaving = false
  }

  initUploader() {
    return new Uploader(
      Object.assign({}, letterConfig.fineUploader, {
        autoUpload: false,
        multiple: false,
        button: this.refs.uploadButton,
        callbacks: {
          onError: this.onUploadError.bind(this),
          onSubmit: this.onFileSubmit.bind(this),
          onSubmitted: this.onFileSubmitted.bind(this),
          onComplete: this.onUploadComplete.bind(this),
        },
        request: {
          endpoint: letterConfig.saveLetterPaperImageUrl,
          customHeaders: { Authorization: `Bearer ${orgamax.user.token}` },
          inputName: 'image',
        },
      }),
    )
  }

  initCanvas() {
    this.canvas = new fabric.Canvas(this.refs.canvas, { selection: false }).setWidth(CANVAS_WIDTH).setHeight(CANVAS_HEIGHT)
    const lineOptions = { stroke: '#ddd', fill: 'transparent', selectable: false, opacity: 0 }
    const wrapperOptions = Object.assign({}, BOUNDING_BOX, lineOptions)
    // Hack: Show border from wrapper container (Canvas renders 0.5 pixels)
    wrapperOptions.width--
    wrapperOptions.height--
    const snapOptions = Object.assign({}, { strokeDashArray: [5] }, lineOptions)

    this.wrapper = new fabric.Rect(wrapperOptions)
    this.vSnap = new fabric.Line(V_SNAP_POINTS, snapOptions)
    this.hSnap = new fabric.Line(H_SNAP_POINTS, snapOptions)

    // initialize canvas with events
    this.canvas
      .add(this.wrapper, this.hSnap, this.vSnap)
      .on('mouse:up', this.onCanvasMouseUp)
      .on('object:moving', this.onCanvasObjectMoving)
      .on('object:scaling', this.onCanvasObjectScaling)
      .on('selection:cleared', this.onCanvasSelectionCleared)
      .on('selection:created', this.onCanvasObjectSelected)
      .on('selection:updated', this.onCanvasObjectSelected)
      .on('before:selection:cleared', this.onCanvasSelectionCleared)
      .on('object:modified', this.onCanvasObjectModified)

    this.addDeselectHandlers()
  }

  addDeselectHandlers() {
    $('.canvas-container').on('mousedown', this.stopPropagation)
    $('.headerEdit').on('mousedown', this.deactivateAll)
  }

  removeDeselectHandlers() {
    $('.canvas-container').off('mousedown', this.stopPropagation)
    $('.headerEdit').off('mousedown', this.deactivateAll)
    return this
  }

  addFabricObjectsToCanvas() {
    const { fabricObjects } = this.state

    fabricObjects.forEach(fabricObject => {
      fabricObject.setColor(fabricObject.color)
      this.canvas.insertAt(fabricObject, fabricObject._sortId, false)
    })

    this.canvas.requestRenderAll()
  }

  onUploadError(id, name, reason, xhr) {
    toast.showToast({ type: 'error', message: reason })
    // this.whenImageUploaded.reject(reason);
  }

  onFileSubmit(id) {
    const uploads = this.uploader.getUploads()
    if (uploads.length > 1) {
      for (let i = 0, il = uploads.length; i < il; i++) {
        uploads[i].id !== id && this.uploader.cancel(uploads[i].id)
      }
    }
  }

  onFileSubmitted(id) {
    const url = URL.createObjectURL(this.uploader.getFile(id))
    this.addImage(url, id)
  }

  onUploadComplete(id, name, response) {
    const letterElement = new LetterElementModel()
    const parsedElement = letterElement.parse(response.data)
    letterElement.set(parsedElement)
    this.whenImageUploaded.resolve(letterElement)
  }

  addImage(url, uploadId) {
    const fetchFabricImageCallback = image => {
      // remove existing image from canvas
      const images = this.canvas.getObjects('image')
      images.forEach((image) => {
        this.canvas.remove(image)
      })

      const imageOptions = {
        width: image.width,
        height: image.height,
        left: image.left + OFFSET,
        top: image.top + OFFSET,
        _sortId: 3,
      }

      const fabricImageOptions = _.assign(imageOptions, letterConfig.fabricOptions)
      image.set(fabricImageOptions).setCoords()

      image._uploadId = uploadId
      this.canvas.insertAt(image, image._sortId)

      // center image to bounding box
      this.adjustImage(image)

      this.canvas.setActiveObject(this.canvas.item(image._sortId))
      image.lockScalingX = false
      image.lockScalingY = false
    }

    fabric.Image.fromURL(url, fetchFabricImageCallback)

  }

  addText() {
    const canvasCenterTop = CANVAS_HEIGHT / 2
    const canvasCenterLeft = CANVAS_WIDTH / 2

    let fabricObjects = this.canvas.getObjects()
    fabricObjects = fabricObjects.slice(CANVAS_ZINDEX_OFFSET)

    const textElements = fabricObjects.reduce((textElementList, obj, index) => {
      const objectType = obj.type

      if (objectType === 'i-text' && !obj._letterElementId) {
        textElementList.push(obj)
      }

      return textElementList
    }, [])

    const font = this.fonts.find((font) => font.default === true)

    const defaultOptions = {
      fontFamily: font.name,
      fontSize: DEFAULT_FONT_SIZE,
      fontStyle: 'normal',
      fontWeight: font.regular,
      left: canvasCenterLeft,
      top: canvasCenterTop,
      lockScalingX: true,
      lockScalingY: true,
      hasControls: false,
    }

    const fabricTextOptions = _.assign({}, defaultOptions, letterConfig.fabricOptions)
    const text = new FabricText('Ihr Text', fabricTextOptions)

    if (this.hasAddedText && textElements.length <= 29) {
      this.addedTextAdditionalTop = textElements.length * 10
      this.addedTextAdditionalLeft = textElements.length * 10

      if (textElements.length >= 27) {
        this.addedTextAdditionalTop = textElements.length * 10 - (27 * 10)
      } else if (textElements.length >= 18) {
        this.addedTextAdditionalTop = textElements.length * 10 - (18 * 10)
      } else if (textElements.length >= 9) {
        this.addedTextAdditionalTop = textElements.length * 10 - (9 * 10)
      }
    }

    this.hasAddedText = true

    text.top -= (text.height / 2) - this.addedTextAdditionalTop
    text.left -= (text.width / 2) - this.addedTextAdditionalLeft

    this.canvas.add(text)
    this.canvas.setActiveObject(text)
    text.enterEditing().selectAll()
  }

  onButtonSave() {
    if (!this.isSaving) {
      this.isSaving = true
      return this.saveLetterElements()
    }
  }

  removeObject() {
    const activeObject = this.canvas.getActiveObject()
    // remove focus from active object because it wouldn't delete it if it's still active
    this.canvas.discardActiveObject()
    this.canvas.remove(activeObject)
    // activeObject.remove()
    this.canvas.requestRenderAll()
  }

  handleFontFamilyChange(newState) {
    this.setState(newState)
  }

  handleFontSizeChange(newState) {
    this.setState(newState)
  }

  showLoader() {
    this.setState({ loading: true })
  }
  hideLoader() {
    this.setState({ loading: false })
  }

  createImages() {
    if (this.prepareImageUploads()) {
      this.uploader.uploadStoredFiles()
      this.whenImageUploaded = q.defer()
    } else {
      // when there are no image uploads, just resolve the promise to complete
      this.whenImageUploaded.resolve()
    }
    // whenImageUploaded used for wrapping the fineUploader callback functions (onComplete, onError)
    return this.whenImageUploaded.promise
  }

  prepareImageUploads() {
    const images = this.canvas.getObjects('image').filter(image => !image._letterElementId)
    if (!images.length) {
      return false
    }

    images.forEach(image => {
      this.uploader.setParams(
        {
          sortId: image._sortId,
          section: 'header',
          x: parseFloat(image.left - OFFSET),
          y: parseFloat(image.top - OFFSET),
          metaData: {
            width: parseFloat(image.getScaledWidth()),
            height: parseFloat(image.getScaledHeight()),
          },
        },
        image._uploadId,
      )
    })
    return true
  }

  saveLetterElements() {
    const { model, collection, onSuccess } = this.props
    let fabricObjects = this.canvas.getObjects()
    fabricObjects = fabricObjects.slice(CANVAS_ZINDEX_OFFSET)

    this.showLoader()

    const saveLetterModel = ([letterElements, letterImage]) => {
      if (letterImage) {
        letterElements.push(letterImage)
      }
      collection.reset(letterElements)

      return request(letterConfig.saveLetterPaperUrl, {
        method: 'POST',
        data: { ...model.letterElements._values, header: letterElements.map(column => column._values) },
        auth: true,
        isConcurrent: true,
      })
    }

    const onSaveSuccess = ({ body: { data: { header } } }) => {
      collection.reset(header)
      toast.showToast({ message: lang.letterHeaderSaveSuccessMessage })
      return collection.models
    }

    const onFinalSuccess = fabricObjects => {
      this.hideLoader()
      onSuccess && onSuccess({ items: collection.models, fabricObjects })
      this.isSaving = false
    }

    const onSaveError = error => {
      toast.showToast({ type: 'error', message: error })
      this.hideLoader()
      this.isSaving = false
    }

    return Promise.resolve(this.parseFabricObjects(fabricObjects))
      .then(letterElements => {
        return Promise.all([letterElements, this.createImages()])
      })
      .then(saveLetterModel)
      .then(onSaveSuccess)
      .then(letterElements => {
        return letterElementsToFabricObjects(letterElements)
      })
      .then(onFinalSuccess)
      .catch(onSaveError)
  }

  parseFabricObjects(fabricObjects) {
    const { collection } = this.props

    const letterElements = fabricObjects.reduce((letterElementList, obj, index) => {
      const letterElementModel = obj._letterElementId ? collection.find({ id: obj._letterElementId }) : undefined
      obj._sortId = index + CANVAS_ZINDEX_OFFSET

      const objectType = obj.type

      switch (objectType) {
        case 'image':
          if (!letterElementModel) return letterElementList
          const imageElement = fabricObjectToImageLetterElement(obj, letterElementModel)
          letterElementList.push(imageElement)
          break
        case 'i-text':
          const textElement = fabricObjectToTextLetterElement(obj, letterElementModel)
          letterElementList.push(textElement)
          break
        case 'rect':
          const rectangleElement = fabricObjectToShapeLetterElement(obj, letterElementModel)
          letterElementList.push(rectangleElement)
          break
      }
      return letterElementList
    }, [])

    return letterElements
  }

  // set opacity on a fabric object
  setOpacity(prop, value) {
    if (this.canvas && this[prop] instanceof fabric.Object) {
      this[prop].opacity = value
      this.canvas.requestRenderAll()
    }
  }

  // adjust position and dimensions of an image
  adjustImage(image) {
    const maxWidth = CANVAS_WIDTH - (OFFSET * 2) - 40
    const maxHeight = CANVAS_HEIGHT - (OFFSET * 2) - 40
    const imageWidth = image.width
    const imageHeight = image.height

    // scale image
    if (imageWidth > maxWidth || imageHeight > maxHeight) {
      const fitToWidth = imageWidth / maxWidth > imageHeight / maxHeight
      image.scaleToWidth(fitToWidth ? maxWidth : imageWidth / imageHeight * maxHeight)
      image.scaleToHeight(fitToWidth ? imageHeight / imageWidth * maxWidth : maxHeight)
      image.setCoords()
    }

    // position image
    image
      .set({
        left: (CANVAS_WIDTH - image.getScaledWidth()) / 2,
        top: (CANVAS_HEIGHT - image.getScaledHeight()) / 2,
      })
      .setCoords()

    // draw changes
    this.canvas.requestRenderAll()
  }

  // restrict objects on canvas so they don't move out of the boundingBox and snap them
  // to the horizontal and vertical centers.
  onCanvasObjectMoving(obj) {
    const object = obj.target
    object.set({
      padding: 0,
      backgroundColor: 'rgba(255, 255, 255, 0.7)',
    })

    // snap to center
    const center = object.getCenterPoint()
    const threshold = 8
    const snapX = _.inRange(center.x, CENTER.x - threshold, CENTER.x + threshold)
    const snapY = _.inRange(center.y, CENTER.y - threshold, CENTER.y + threshold)
    this.setState({ hSnapVisible: snapY, vSnapVisible: snapX, wrapperVisible: true })
    object.setPositionByOrigin(new fabric.Point(snapX ? CENTER.x : center.x, snapY ? CENTER.y : center.y), 'center', 'center')
  }

  onCanvasObjectScaling(obj) {
    // Images always have a height of 158 when newly added. So if it gets scaled bigger than 1137mm in height,
    // I set the scale to 7.2 so it wont become bigger.
    // After saving an image it gets its real height, so if the height of the picture is 1137mm or bigger, then it was added before.
    // Setting scale factor to 1 because this is the maximum width/height factor.
    const object = obj.target
    object.set({
      padding: 0,
      backgroundColor: 'rgba(255, 255, 255, 0.7)',
    })

    if (object.scaleY >= 7.2) {
      object.set({ scaleY: 7.2, scaleX: 7.2 })
    } else if (object.height >= 1137) {
      object.set({ scaleY: 1, scaleX: 1 })
    }
    this.setState({ wrapperVisible: true })
  }

  onCanvasMouseUp({ e: event }) {
    const object = this.canvas.getActiveObject()
    const wrapThreshold = 20

    if (object) {
      object.set({
        backgroundColor: 'transparent',
      })

      object.setCoords()

      if (object.getBoundingRect().top < 0 - object.getBoundingRect().height + wrapThreshold) {
        object.top = 0 - object.getBoundingRect().height + wrapThreshold
      }

      if (object.getBoundingRect().left < 0 - object.getBoundingRect().width + wrapThreshold) {
        object.left = 0 - object.getBoundingRect().width + wrapThreshold
      }

      if (object.getBoundingRect().top > object.canvas.height - wrapThreshold) {
        object.top = object.canvas.height - wrapThreshold
      }

      if (object.getBoundingRect().left > object.canvas.width - wrapThreshold) {
        object.left = object.canvas.width - wrapThreshold
      }

      object.setCoords()
    }

    this.setState({ hSnapVisible: false, vSnapVisible: false, wrapperVisible: false })
  }

  onCanvasObjectSelected(obj) {
    this.onCanvasSelectionCleared(obj.target)
    const target = obj.target
    if (target.type === 'image') {
      target.lockScalingX = false
      target.lockScalingY = false
      target.hasControls = true
      target.selectable = true
    }
    this.updateSelectionStates(target)
  }

  onCanvasSelectionCleared(target) {
    const { previousEl } = this.state
    const fontSize = target?.fontSize ? target?.fontSize : previousEl?.fontSize ? previousEl?.fontSize : undefined
    this.setState(
      {
        selectedFont: undefined,
        selectedFontSize: fontSize,
        selectedFontColor: undefined,
        deleteButtonActive: false,
        textSelected: false,
        canHaveColor: false,
        textIsBold: false,
        textIsItalic: false,
        textIsUnderlined: false,
        textSupportsBold: false,
        textSupportsItalic: false,
      },
      () => {
        if (previousEl && previousEl.type === 'i-text') {
          // remove the active class from the font family select input because the blur event doesn't get triggered correctly
          const fontFamilyInput = $('.fontFamilySelect')
          fontFamilyInput.removeClass('selectInput-active')

          // set a invalid fontSize of the previous selected element to a valid size
          const fontSize = previousEl.fontSize
          if (fontSize > 100) {
            previousEl.set('fontSize', 100)
          } else if (fontSize < 10) {
            previousEl.set('fontSize', 10)
          }
        }
      },
    )
  }

  onCanvasObjectModified({ target }) {
    const activeObject = this.canvas.getActiveObject()
    if (activeObject === target) {
      this.updateSelectionStates(target)
    }
  }

  updateSelectionStates(target) {
    let options = {}
    const textSelected = target instanceof fabric.IText
    const canHaveColor = target instanceof fabric.IText || target instanceof fabric.Rect

    if (textSelected) {
      target.hasControls = false
      const font = this.fonts.find(({ name }) => name === target.fontFamily)
      options = {
        selectedFont: font,
        selectedFontSize: target.fontSize,
        textIsBold: target.fontWeight === font.bold,
        textIsItalic: target.fontStyle === 'italic',
        textIsUnderlined: target.underline,
        textSupportsItalic: font.italic,
        textSupportsBold: font.bold,
      }
    }

    if (canHaveColor) {
      Object.assign(options, { selectedFontColor: target.fill })
    }

    const state = Object.assign(
      {},
      {
        deleteButtonActive: true,
        previousEl: target,
        textSelected,
        canHaveColor,
      },
      options,
    )

    this.setState(state)
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  deactivateAll() {
    this.canvas.deactivateAllWithDispatch().requestRenderAll()
  }

  handleKeydown(event) {
    const { keyCode } = event
    const { onCancelEditMode } = this.props
    const obj = this.canvas.getActiveObject()

    if (keyCode === KEY_CODES.ESCAPE) {
      return onCancelEditMode && onCancelEditMode()
    }

    if (!obj) {
      return
    }

    switch (keyCode) {
      case KEY_CODES.ENTER:
        if (obj instanceof fabric.IText) {
          obj.enterEditing().selectAll()
          event.preventDefault()
        }
        break
      case KEY_CODES.DELETE:
        this.removeObject()
        break
      case KEY_CODES.LEFT:
        obj.left -= event.shiftKey ? SHIFT_STEP : 1
        break
      case KEY_CODES.TOP:
        obj.top -= event.shiftKey ? SHIFT_STEP : 1
        break
      case KEY_CODES.RIGHT:
        obj.left += event.shiftKey ? SHIFT_STEP : 1
        break
      case KEY_CODES.DOWN:
        obj.top += event.shiftKey ? SHIFT_STEP : 1
        break
    }

    obj.setCoords()
    this.canvas.requestRenderAll()
  }

  render() {
    const {
      loading,
      textSelected,
      canHaveColor,
      deleteButtonActive,
      hSnapVisible,
      vSnapVisible,
      wrapperVisible,
      textIsBold,
      textIsItalic,
      textIsUnderlined,
      textSupportsBold,
      textSupportsItalic,
      selectedFontSize,
      selectedFont,
      selectedFontColor,
    } = this.state
    const { showSaveButton } = this.props

    // this needs to be a div with the appearance of an button - using <button> will not work with firefox

    const uploadButton = <div ref='uploadButton' className='button button-primary button-small button-icon-picture' disabled={loading} data-qs-id='settings-letter-paper-logo-upload'>Logo hochladen</div>
    this.setOpacity('hSnap', +hSnapVisible)
    this.setOpacity('vSnap', +vSnapVisible)
    this.setOpacity('wrapper', +wrapperVisible)

    const loader = loading
      ? <Loader text='' visible={loading} />
      : null

    return (
      <div>
        {loader}
        <div className={`headerEdit document-edit ${wrapperVisible ? 'headerEdit-active' : ''}`}>
          <canvas ref='canvas' />
        </div>
        <form ref='form' className='form letterHeader_tools'>
          <button ref='formSubmit' className='u_hidden' />
          <HeaderFontTools
            canvas={this.canvas}
            fonts={this.fonts}
            textSelected={textSelected}
            canHaveColor={canHaveColor}
            textIsBold={textIsBold}
            textIsItalic={textIsItalic}
            textIsUnderlined={textIsUnderlined}
            textSupportsBold={textSupportsBold}
            textSupportsItalic={textSupportsItalic}
            selectedFont={selectedFont}
            selectedFontColor={selectedFontColor}
            selectedFontSize={selectedFontSize}
            handleFontFamilyChange={this.handleFontFamilyChange.bind(this)}
            handleFontSizeChange={this.handleFontSizeChange.bind(this)} />
          <div className='buttonRow'>
            <div className='u_wc'>
              {uploadButton}
              <button type='button' onClick={this.addText.bind(this)} className='button button-primary button-small button-icon-font' data-qs-id='settings-letter-paper-logo-text'>
                                Text
              </button>
              <button
                type='button'
                disabled={!deleteButtonActive}
                onClick={this.removeObject.bind(this)}
                className='button button-primary button-small button-icon-trashcan'
                data-qs-id='settings-letter-paper-logo-delete'
              >
                                Löschen
              </button>
              {showSaveButton && (<div className='headerEditSaveButton'>
                <button type='button' onClick={this.onButtonSave.bind(this)} className='button button-primary button-small button-icon-font' data-qs-id='settings-letter-paper-logo-save'>
                                Speichern
                </button>
              </div>)}
            </div>
          </div>
        </form>
      </div>
    )
  }
}

HeaderEdit.propTypes = {
  items: PropTypes.array,
  model: PropTypes.object,
  collection: PropTypes.object,
  onSuccess: PropTypes.func,
  showSaveButton: PropTypes.bool,
  onCancelEditMode: PropTypes.func.isRequired,
}

export default HeaderEdit
